import { useTheme } from "../context/ThemeContext";
import { useState, useEffect } from "react";
import { getBudgetSummary, getBudgetHealth, getTransactions, syncPlaid, getIncome, getHousehold, expandSplits } from "../api/client";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const fmt  = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtK = (n) => Math.abs(n) >= 1000 ? `$${(Math.abs(n)/1000).toFixed(1)}k` : fmt(n);
const pct  = (a, b) => b === 0 ? 0 : Math.min(100, Math.round((a / b) * 100));
// The user's local date — sent to the server, whose UTC clock may already be on tomorrow.
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const WEEKDAY_INDEX = { Sunday:0, Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6 };
const countWeekdaysInRange = (weekday, start, end) => {
  const target = WEEKDAY_INDEX[weekday];
  if (target === undefined) return 4;
  let count = 0;
  const d = new Date(start);
  while (d.getDay() !== target) d.setDate(d.getDate() + 1);
  while (d <= end) { count++; d.setDate(d.getDate() + 7); }
  return count;
};

const buildChartData = (transactions, categories, periodStart, periodLength) => {
  const today = new Date();
  // Count split slices in their own categories, not the parent's single category.
  transactions = expandSplits(transactions);

  // Split categories by type
  const fixedCats   = categories.filter(c => c.type === "fixed");
  const varCats     = categories.filter(c => c.type !== "fixed");
  const varBudget   = varCats.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const dailyVar    = varBudget / periodLength;

  // Build planned spend by day — fixed jump on due day, variable spread evenly
  const plannedByDay = {};
  for (let d = 1; d <= periodLength; d++) plannedByDay[d] = dailyVar;

  fixedCats.forEach(cat => {
    const dueDay = parseInt(cat.due_day);
    const amt    = parseFloat(cat.amount || 0);
    if (!dueDay || !amt) {
      // No due day — spread evenly
      for (let d = 1; d <= periodLength; d++) plannedByDay[d] += amt / periodLength;
      return;
    }
    // Convert calendar due_day to period offset day
    // Period starts on periodStart (e.g. Apr 26)
    // due_day is a calendar day (1-31)
    const periodStartDay = periodStart.getDate();
    const periodStartMonth = periodStart.getMonth();
    const periodStartYear  = periodStart.getFullYear();

    let dueDate;
    if (dueDay >= periodStartDay) {
      // Due day falls in same month as period start
      dueDate = new Date(periodStartYear, periodStartMonth, dueDay);
    } else {
      // Due day falls in next month
      dueDate = new Date(periodStartYear, periodStartMonth + 1, dueDay);
    }

    const offset = Math.round((dueDate - periodStart) / 86400000) + 1;
    if (offset >= 1 && offset <= periodLength) {
      plannedByDay[offset] = (plannedByDay[offset] || 0) + amt;
    }
  });

  // Build actual spend by day
  const spendByDay = {}, varSpendByDay = {};
  transactions.filter(t => t.type === "expense").forEach(t => {
    const txDate = new Date(t.date);
    txDate.setHours(0,0,0,0);
    const d = Math.round((txDate - periodStart) / 86400000) + 1;
    if (d >= 1 && d <= periodLength) {
      const amt = parseFloat(t.amount);
      spendByDay[d] = (spendByDay[d] || 0) + amt;
      const cat = categories.find(c => c.id === t.category_id);
      if (cat && cat.type !== "fixed") varSpendByDay[d] = (varSpendByDay[d] || 0) + amt;
    }
  });

  const todayOffset = Math.round((today - periodStart) / 86400000) + 1;
  const data = [];
  let cumPlanned = 0, cumActual = 0, cumVarPlanned = 0, cumVar = 0;

  for (let d = 1; d <= periodLength; d++) {
    cumPlanned    += plannedByDay[d] || 0;
    cumActual     += spendByDay[d] || 0;
    cumVarPlanned += dailyVar;
    cumVar        += varSpendByDay[d] || 0;
    data.push({
      day: d,
      totalBudget: Math.round(cumPlanned),
      totalActual: d <= todayOffset ? Math.round(cumActual) : null,
      varBudget:   Math.round(cumVarPlanned),
      varActual:   d <= todayOffset ? Math.round(cumVar) : null,
    });
  }
  return data;
};

const CustomTooltip = ({ active, payload, label }) => {
  const { COLORS } = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 14px" }}>
      <p style={{ color: COLORS.textMuted, fontSize: 11, margin: "0 0 6px" }}>Day {label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: 12, fontWeight: 700, margin: "2px 0", fontFamily: "monospace" }}>
          {p.name}: {fmtK(p.value)}
        </p>
      ))}
    </div>
  );
};

const SpendingChart = ({ title, subtitle, data, budgetKey, actualKey, budgetColor, actualColor }) => {
  const { COLORS } = useTheme();
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: 0 }}>{title}</h3>
        <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "3px 0 0" }}>{subtitle}</p>
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <AreaChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
          <defs>
            <linearGradient id={`gb-${budgetKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={budgetColor} stopOpacity={0.12} />
              <stop offset="95%" stopColor={budgetColor} stopOpacity={0} />
            </linearGradient>
            <linearGradient id={`ga-${actualKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={actualColor} stopOpacity={0.18} />
              <stop offset="95%" stopColor={actualColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="day" tick={{ fill: COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} interval={6} />
          <YAxis tick={{ fill: COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => fmtK(v)} />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey={budgetKey} name="Planned" stroke={budgetColor} strokeWidth={1.5} strokeDasharray="5 3" fill={`url(#gb-${budgetKey})`} dot={false} connectNulls />
          <Area type="monotone" dataKey={actualKey} name="Actual" stroke={actualColor} strokeWidth={2} fill={`url(#ga-${actualKey})`} dot={false} connectNulls />
        </AreaChart>
      </ResponsiveContainer>
      <div style={{ display: "flex", gap: 14, marginTop: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 18, height: 2, background: budgetColor, opacity: 0.6 }} />
          <span style={{ color: COLORS.textMuted, fontSize: 11 }}>Planned</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 18, height: 2, background: actualColor }} />
          <span style={{ color: COLORS.textMuted, fontSize: 11 }}>Actual</span>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ setScreen }) => {
  const { COLORS } = useTheme();
  const [summary,      setSummary]      = useState(null);
  const [health,       setHealth]       = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [incomes,      setIncomes]      = useState([]);
  const [household,    setHousehold]    = useState(null);
  const [syncing,      setSyncing]      = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [expenseFilter, setExpenseFilter] = useState("all");
  const [pennyInsight,  setPennyInsight]  = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await getHousehold();    const d = r.data; setHousehold(Array.isArray(d) ? null : d?.data || d || null); } catch(e) {}
    try { const r = await getBudgetSummary(); setSummary(r.data); } catch(e) {}
    // Type-aware pace/projection (fixed = posted or not, variable = real pace, misc =
    // balance drawdown) — see budgetHealth() in projection.js. Drives the headline
    // number and per-category badges below; `summary` above still feeds the charts.
    try { const r = await getBudgetHealth({ today: localToday() }); setHealth(r.data); } catch(e) {}
    try { const r = await getIncome();       const d = r.data; setIncomes(Array.isArray(d) ? d : d?.data || []); } catch(e) {}
    try { const r = await getTransactions(); const d = r.data; setTransactions(Array.isArray(d) ? d : d?.transactions || d?.data || []); } catch(e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try { await syncPlaid(); await load(); } catch(e) {}
    finally { setSyncing(false); }
  };

  const fetchPenny = async (cats, totalSpent, totalBudget, dayOfPeriod, periodLength) => {
    try {
      const topCats = cats.slice(0,6).map(c => `${c.name}: $${parseFloat(c.spent||0).toFixed(0)}/$${parseFloat(c.amount||0).toFixed(0)}`).join(", ");
      const prompt = `You are Penny, a friendly household budget advisor. Give ONE short, specific, actionable insight (max 2 sentences) based on this data. Day ${dayOfPeriod} of ${periodLength}. Spent $${totalSpent.toFixed(0)} of $${totalBudget.toFixed(0)} budget. Categories: ${topCats}. Be direct and helpful. Return only the insight text, no JSON.`;
      const res = await fetch("/api/household/ai-analysis", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("ledger_token")}` },
        body: JSON.stringify({ prompt })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "";
      if (text) setPennyInsight(text);
    } catch(e) {}
  };

  if (loading) return (
    <div style={{ padding: 24, color: COLORS.textMuted, textAlign: "center", paddingTop: 80 }}>Loading...</div>
  );

  const cats        = summary?.categories || [];
  const monthStart  = household?.month_start || 26;
  const today       = new Date();
  const periodStart = today.getDate() >= monthStart
    ? new Date(today.getFullYear(), today.getMonth(), monthStart)
    : new Date(today.getFullYear(), today.getMonth() - 1, monthStart);
  periodStart.setHours(0,0,0,0);
  const periodEndDate = new Date(periodStart.getFullYear(), periodStart.getMonth() + 1, monthStart - 1);
  periodEndDate.setHours(23,59,59,999);
  const periodLength  = Math.round((periodEndDate - periodStart) / 86400000) + 1;
  const dayOfPeriod   = Math.min(Math.round((today - periodStart) / 86400000) + 1, periodLength);
  const daysLeft      = periodLength - dayOfPeriod;

  const periodTxns = expandSplits(transactions).filter(t => {
    const [y,m,d] = (t.date||"").split("T")[0].split("-");
    if (!y) return false;
    const txDate = new Date(parseInt(y), parseInt(m)-1, parseInt(d));
    return txDate >= periodStart && txDate <= periodEndDate;
  });

  const budgetTxns = periodTxns.filter(t => t.type === "expense" && t.category_id !== 41);
  const spentByCat = budgetTxns.reduce((acc, t) => {
    if (t.category_id) acc[t.category_id] = (acc[t.category_id] || 0) + parseFloat(t.amount);
    return acc;
  }, {});
  const periodCats   = cats.map(c => ({ ...c, spent: spentByCat[c.id] || 0 }));
  const moneyIn      = periodTxns.filter(t => t.type === "income").reduce((s,t) => s + parseFloat(t.amount), 0);

  // Type-aware budget health (server-computed, see projection.js's budgetHealth()) —
  // replaces the old single blended "spent vs day-of-period" formula, which made
  // fixed bills posting on day 1 look "way ahead of pace" when nothing was wrong.
  // `healthByCat` looks up each category's own fixed/variable/misc-appropriate status
  // by id; the totals below already exclude excluded_from_pace (windfall) spending.
  const healthLines   = health?.lines || [];
  const healthByCat   = new Map(healthLines.map(l => [l.id, l]));
  const totalBudget   = health?.totalBudgeted  ?? 0;
  const totalSpent    = health?.totalActual    ?? 0;
  const totalProjected = health?.totalProjected ?? totalBudget;
  const healthStatus  = health?.status || 'on_track';
  const lowConfidence = health?.lowConfidence ?? true;
  const remaining     = totalBudget - totalSpent;
  const remainingPct  = pct(totalSpent, totalBudget);
  const statusColor   = healthStatus === 'over' ? COLORS.red : healthStatus === 'watch' ? COLORS.amber : COLORS.green;

  // expectedPct is the "goal line" plotted on top of variable categories' progress
  // bars — sourced from the server's own elapsed/total days so it can never drift
  // from what the pace statuses above were computed against.
  const healthTotalDays = health?.totalDays || periodLength;
  const healthElapsedDays = health?.elapsedDays || dayOfPeriod;
  const expectedPct  = Math.min(100, Math.round((healthElapsedDays / healthTotalDays) * 100));

  const periodEnd = new Date(periodStart);
  periodEnd.setDate(periodEnd.getDate() + periodLength - 1);
  const plannedIncome = incomes.reduce((sum, src) => {
    const amt = parseFloat(src.amount || 0);
    if (src.schedule_type === "monthly")      return sum + amt;
    if (src.schedule_type === "twiceMonthly") return sum + (amt * 2);
    if (src.schedule_type === "weekly")       return sum + (amt * countWeekdaysInRange(src.week_day, periodStart, periodEnd));
    if (src.schedule_type === "biweekly")     return sum + (amt * Math.ceil(countWeekdaysInRange(src.week_day, periodStart, periodEnd) / 2));
    return sum + amt;
  }, 0);

  const fixedCats    = periodCats.filter(c => c.type === "fixed");
  const fixedTotal   = fixedCats.reduce((s,c) => s + parseFloat(c.amount||0), 0);
  const fixedPaid    = fixedCats.reduce((s,c) => s + parseFloat(c.spent||0), 0);
  const fixedUnpaid  = Math.max(0, fixedTotal - fixedPaid);
  const chartData    = buildChartData(budgetTxns, periodCats, periodStart, periodLength);

  // Bar/headline color now comes from the type-aware status, not a raw %-used
  // threshold — a bar that's 90% full on day 3 (rent already posted) isn't actually
  // a warning sign the way it would be for continuous variable spending.
  const barColor = statusColor;

  const displayCats = expenseFilter === "fixed"
    ? periodCats.filter(c => c.type === "fixed")
    : expenseFilter === "variable"
    ? periodCats.filter(c => c.type === "variable" || c.type === "misc")
    : periodCats;

  if (!pennyInsight && periodCats.length > 0) {
    fetchPenny(periodCats, totalSpent, totalBudget, dayOfPeriod, periodLength);
  }

  return (
    <div style={{ padding: "0 0 100px", fontFamily: "'DM Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ padding: "20px 16px 10px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", margin: 0 }}>
            {today.toLocaleString("default", { month: "long", year: "numeric" })}
          </p>
          <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: "4px 0 0", letterSpacing: -0.5 }}>Overview</h2>
        </div>
        <button onClick={handleSync} disabled={syncing} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", color: COLORS.textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginTop: 4 }}>
          {syncing ? "Syncing..." : "Sync"}
        </button>
      </div>

      <div style={{ padding: "0 12px", display: "flex", flexDirection: "column", gap: 10 }}>

        {/* Budget health headline — "on pace to finish at $X" instead of the old
            blended "spent vs day-of-period" pace, which read a bill posting on day 1
            as "way ahead of pace" even though nothing was actually wrong. */}
        <div style={{ background: healthStatus === 'over' ? COLORS.redGlow : healthStatus === 'watch' ? "rgba(245,158,11,0.08)" : COLORS.greenGlow, border: `1px solid ${statusColor}44`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                {lowConfidence ? "Budget This Period" : "On Pace To Finish At"}
              </div>
              <div style={{ color: statusColor, fontSize: 28, fontWeight: 800, fontFamily: "monospace", letterSpacing: -1 }}>
                {fmt(lowConfidence ? remaining : totalProjected)}
              </div>
              {!lowConfidence && (
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>of {fmt(totalBudget)} budgeted</div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 5 }}>Day {dayOfPeriod} of {periodLength}</div>
              <div style={{ color: barColor, fontSize: 13, fontWeight: 700 }}>{remainingPct}% used</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 600, marginTop: 3 }}>
                {lowConfidence ? "Too early to project" : `${fmt(Math.abs(remaining))} ${remaining >= 0 ? "left" : "over"}`}
              </div>
            </div>
          </div>
          <div style={{ height: 7, background: COLORS.border, borderRadius: 99, overflow: "hidden" }}>
            <div style={{ width: `${remainingPct}%`, height: "100%", background: barColor, borderRadius: 99, transition: "width 0.6s ease" }} />
          </div>
        </div>

        {/* 4 stat cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Expected Expenses</div>
            <div style={{ color: COLORS.text, fontSize: 18, fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalBudget)}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 3 }}>Planned this period</div>
          </div>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Spent So Far</div>
            <div style={{ color: COLORS.amber, fontSize: 18, fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalSpent)}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 3 }}>{remainingPct}% of budget</div>
          </div>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Expected Income</div>
            <div style={{ color: COLORS.green, fontSize: 18, fontWeight: 700, fontFamily: "monospace" }}>{fmt(plannedIncome)}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 3 }}>This period</div>
          </div>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Income Received</div>
            <div style={{ color: moneyIn >= plannedIncome ? COLORS.green : COLORS.text, fontSize: 18, fontWeight: 700, fontFamily: "monospace" }}>{fmt(moneyIn)}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 3 }}>{pct(moneyIn, plannedIncome)}% received</div>
          </div>
        </div>

        {/* Expenses section */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>Expenses</h3>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setExpenseFilter("all")} style={{ padding: "5px 10px", borderRadius: 20, border: `1px solid ${expenseFilter === "all" ? COLORS.accent : COLORS.border}`, background: expenseFilter === "all" ? COLORS.accentGlow : "transparent", color: expenseFilter === "all" ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 11 }}>All</button>
              <button onClick={() => setExpenseFilter("fixed")} style={{ padding: "5px 10px", borderRadius: 20, border: `1px solid ${expenseFilter === "fixed" ? COLORS.accent : COLORS.border}`, background: expenseFilter === "fixed" ? COLORS.accentGlow : "transparent", color: expenseFilter === "fixed" ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 11 }}>Fixed</button>
              <button onClick={() => setExpenseFilter("variable")} style={{ padding: "5px 10px", borderRadius: 20, border: `1px solid ${expenseFilter === "variable" ? COLORS.accent : COLORS.border}`, background: expenseFilter === "variable" ? COLORS.accentGlow : "transparent", color: expenseFilter === "variable" ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 11 }}>Variable</button>
            </div>
          </div>

          {/* Fixed summary when fixed filter active */}
          {expenseFilter === "fixed" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1, background: COLORS.greenGlow, border: `1px solid ${COLORS.green}33`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ color: COLORS.green, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 4 }}>Paid</div>
                <div style={{ color: COLORS.green, fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{fmt(fixedPaid)}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>{fixedCats.filter(c => parseFloat(c.spent||0) >= parseFloat(c.amount||0)*0.9).length} of {fixedCats.length} bills</div>
              </div>
              <div style={{ flex: 1, background: fixedUnpaid > 0 ? "rgba(245,158,11,0.08)" : COLORS.greenGlow, border: `1px solid ${fixedUnpaid > 0 ? COLORS.amber : COLORS.green}33`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ color: fixedUnpaid > 0 ? COLORS.amber : COLORS.green, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 4 }}>Still Due</div>
                <div style={{ color: fixedUnpaid > 0 ? COLORS.amber : COLORS.green, fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{fmt(fixedUnpaid)}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>{fixedCats.filter(c => parseFloat(c.spent||0) < parseFloat(c.amount||0)*0.9).length} bills pending</div>
              </div>
            </div>
          )}

          {displayCats.length === 0 && (
            <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>No categories yet — add them in Settings</p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 420, overflowY: "auto", scrollbarWidth: "none" }}>
            {displayCats.map((cat, i) => {
              const spent     = parseFloat(cat.spent||0);
              const budgeted  = parseFloat(cat.amount||0);
              const p         = pct(spent, budgeted);
              const isVariable = cat.type === 'variable';
              const isFixed    = cat.type === 'fixed';

              // Status comes straight from the server's type-aware budgetHealth() —
              // fixed: paid/due/unpaid/over, variable: ahead/on_track/watch/over,
              // misc: tracked/over. No client-side thresholds anymore.
              const status = healthByCat.get(cat.id)?.status;
              const isOver = status === 'over';

              // "ahead"/"on_track" for variable render no badge at all (quiet unless
              // there's something worth flagging, same philosophy as before). Variable's
              // own 'over' is labeled "over pace" (mid-period pace warning) — distinct
              // from fixed/misc's flat "over" (budget for the whole period exceeded).
              let badgeLabel = null, badgeColor = null;
              if (isFixed) {
                if (status === 'paid')        { badgeLabel = 'paid';   badgeColor = COLORS.green; }
                else if (status === 'unpaid') { badgeLabel = 'unpaid'; badgeColor = COLORS.red; }
                else if (status === 'over')   { badgeLabel = 'over';   badgeColor = COLORS.red; }
                else                          { badgeLabel = 'due';    badgeColor = COLORS.amber; }
              } else if (isVariable) {
                if (status === 'over')       { badgeLabel = 'over pace';  badgeColor = COLORS.red; }
                else if (status === 'watch') { badgeLabel = 'watch pace'; badgeColor = COLORS.amber; }
              } else if (status === 'over') { badgeLabel = 'over'; badgeColor = COLORS.red; }

              const barC = isOver ? COLORS.red : status === 'watch' ? COLORS.amber : COLORS.accent;

              return (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isFixed || cat.type === 'misc' ? 0 : 5, gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, flex: 1 }}>
                      <span style={{ color: COLORS.text, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>{cat.name}</span>
                      {badgeLabel && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 5px", borderRadius: 4, background: badgeColor + "22", color: badgeColor, border: `1px solid ${badgeColor}33`, flexShrink: 0 }}>
                          {badgeLabel}
                        </span>
                      )}
                    </div>
                    <span style={{ color: isOver ? COLORS.red : COLORS.text, fontFamily: "monospace", fontSize: 13, flexShrink: 0, whiteSpace: "nowrap" }}>
                      {fmt(spent)}{cat.type !== 'misc' && budgeted > 0 && <span style={{ color: COLORS.textMuted }}> / {fmt(budgeted)}</span>}
                    </span>
                  </div>
                  {/* Only variable spending accrues continuously enough for a progress
                      bar + pace marker to mean anything — fixed is a checklist
                      (paid/due/unpaid above), misc is a balance draw-down with no
                      target to bar against. */}
                  {isVariable && (
                    <div style={{ position: "relative" }}>
                      <div style={{ height: 5, background: COLORS.border, borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ width: `${Math.min(100,p)}%`, height: "100%", background: barC, borderRadius: 99, transition: "width 0.4s ease" }} />
                      </div>
                      {p < 100 && (
                        <div style={{ position: "absolute", top: -1.5, left: `${expectedPct}%`, width: 2, height: 8, background: COLORS.text, opacity: 0.4, borderRadius: 1, transform: "translateX(-1px)" }} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Charts */}
        <SpendingChart
          title="Total Spending"
          subtitle="Fixed + variable vs full monthly budget"
          budgetKey="totalBudget" actualKey="totalActual"
          budgetColor={COLORS.accent} actualColor={COLORS.amber}
          data={chartData}
        />
        <SpendingChart
          title="Variable Spending"
          subtitle="Groceries, dining, misc — where it gets away from you"
          budgetKey="varBudget" actualKey="varActual"
          budgetColor={COLORS.green} actualColor={COLORS.amber}
          data={chartData}
        />

        {/* Penny insight — moved to the bottom */}
        {pennyInsight && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "14px 16px", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: COLORS.accent, fontWeight: 700, fontSize: 14 }}>P</div>
            <div>
              <div style={{ color: COLORS.accent, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>Penny</div>
              <div style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 1.6 }}>{pennyInsight}</div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
